import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { proposalsApi } from "../lib/proposalsApi";
import type { ProposalDetail } from "../types/proposals";
import toast from "react-hot-toast";
import {
  presentationDraftIssues,
  proposalSaveError,
} from "../lib/proposalPresentationDraft";
import type { PresentationOptions } from "../types/proposalPresentation";

export function preserveDraft(
  server: Partial<ProposalDetail>,
  dirty: Record<string, unknown>,
): Partial<ProposalDetail> {
  return { ...server, ...dirty };
}
export function remainingEdits(
  dirty: Record<string, unknown>,
  submitted: Record<string, unknown>,
) {
  return Object.fromEntries(
    Object.entries(dirty).filter(
      ([key, value]) =>
        JSON.stringify(value) !== JSON.stringify(submitted[key]),
    ),
  );
}

export function useProposalDraft(
  proposal: ProposalDetail | undefined,
  id: string | undefined,
) {
  const client = useQueryClient();
  const [formData, setFormData] = useState<Partial<ProposalDetail>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [recovery, setRecovery] = useState<Record<string, unknown> | null>(
    null,
  );
  const dirty = useRef<Record<string, unknown>>({});
  const loaded = useRef<number | null>(null);
  const request = useRef<Promise<unknown> | null>(null);
  const serviceVersion = useRef(proposal?.service_version);
  const storageKey = `apexos:proposal-draft:${id}`;
  const store = useCallback(() => {
    try {
      if (Object.keys(dirty.current).length)
        localStorage.setItem(storageKey, JSON.stringify(dirty.current));
      else localStorage.removeItem(storageKey);
    } catch {
      /* Navigation protection still works when storage is unavailable. */
    }
  }, [storageKey]);
  const updateMutation = useMutation({
    mutationKey: ["proposal-editor", id],
    mutationFn: (fields: Record<string, unknown>) =>
      proposalsApi.update(Number(id), fields),
    onSuccess: (saved, submitted) => {
      if (loaded.current !== saved.id) return;
      dirty.current = remainingEdits(dirty.current, submitted);
      serviceVersion.current = saved.service_version;
      if ('service_configuration' in dirty.current) dirty.current.service_base_version = saved.service_version;
      else delete dirty.current.service_base_version;
      setHasChanges(Object.keys(dirty.current).length > 0);
      setFormData(preserveDraft(saved, dirty.current));
      store();
      client.setQueryData(["proposal", id], saved);
      client.invalidateQueries({ queryKey: ["proposal-editor-history", id] });
      client.invalidateQueries({ queryKey: ["proposals"] });
    },
    onError: (error) =>
      toast.error(
        `${proposalSaveError(error)} Your draft is retained; retry Save before sharing.`,
      ),
  });
  useEffect(() => {
    if (!proposal || String(proposal.id) !== id) return;
    if (loaded.current !== proposal.id) {
      loaded.current = proposal.id;
      dirty.current = {};
      serviceVersion.current = proposal.service_version;
      setHasChanges(false);
      try {
        setRecovery(JSON.parse(localStorage.getItem(storageKey) || "null"));
      } catch {
        setRecovery(null);
      }
    }
    if (!('service_configuration' in dirty.current)) serviceVersion.current = proposal.service_version;
    setFormData(preserveDraft(proposal, dirty.current));
  }, [proposal, id, storageKey]);
  const handleFieldChange = (field: string, value: unknown) => {
    if (field === 'service_configuration' && !('service_configuration' in dirty.current)) {
      dirty.current.service_base_version = serviceVersion.current;
    }
    dirty.current = { ...dirty.current, [field]: value };
    setFormData((previous) => preserveDraft(previous, dirty.current));
    setHasChanges(true);
    updateMutation.reset();
    store();
  };
  const handleSave = async (): Promise<void> => {
    if (request.current) {
      await request.current;
      return latestSave.current();
    }
    if (!Object.keys(dirty.current).length || !id) return;
    const issue = Object.values(
      presentationDraftIssues(
        dirty.current.presentation_options as PresentationOptions | undefined,
      ),
    )[0];
    if (issue)
      throw new Error(`${issue} Your draft is retained in this browser.`);
    const submitted = { ...dirty.current };
    const pending = updateMutation.mutateAsync(submitted);
    request.current = pending;
    try {
      await pending;
    } catch (error) {
      throw new Error(proposalSaveError(error));
    } finally {
      if (request.current === pending) request.current = null;
    }
    if (Object.keys(dirty.current).length) await latestSave.current();
  };
  const latestSave = useRef(handleSave);
  latestSave.current = handleSave;
  const validationMessage = hasChanges
    ? Object.values(
        presentationDraftIssues(formData.presentation_options),
      )[0] || null
    : null;
  useEffect(() => {
    if (
      !hasChanges ||
      validationMessage ||
      updateMutation.isError ||
      proposal?.acceptance_date ||
      proposal?.status === "accepted"
    )
      return;
    const timer = setTimeout(() => {
      latestSave.current().catch(() => {});
    }, 900);
    return () => clearTimeout(timer);
  }, [
    formData,
    hasChanges,
    validationMessage,
    updateMutation.isError,
    proposal?.acceptance_date,
    proposal?.status,
  ]);
  return {
    formData,
    hasChanges,
    isDirty: () => Object.keys(dirty.current).length > 0,
    handleFieldChange,
    handleSave,
    updateMutation,
    validationMessage,
    saveError: updateMutation.isError
      ? proposalSaveError(updateMutation.error)
      : null,
    recovery,
    recover: () => {
      const fields = recovery || {};
      setRecovery(null);
      for (const [key, value] of Object.entries(fields))
        handleFieldChange(key, value);
    },
    discardRecovery: () => {
      setRecovery(null);
      try {
        localStorage.removeItem(storageKey);
      } catch {}
    },
  };
}
